// Statistics Tab Component - Company-wide sales metrics dashboard

import React, { useEffect, useState, useCallback } from 'react';
import { statisticsService } from '../../services/statistics';
import type {
  OverviewStats,
  TrendDataPoint,
  SalespersonStats,
  QuotePipelineStats,
  RoutingSummary,
  FilterOptions,
  StatsFilters,
} from '../../services/statistics';
import { formatCurrency, getChangeDirection, getChangeArrow } from '../../utils/formatting';
import CostsPanel from './CostsPanel';
import OrdersPanel from './OrdersPanel';
import EmailPipelinePanel from './EmailPipelinePanel';
import SystemHealthPanel from './SystemHealthPanel';
import DataFreshnessPanel from './DataFreshnessPanel';
import DocumentPipelinePanel from './DocumentPipelinePanel';
import InboxResponseTimePanel from './InboxResponseTimePanel';
import RoiSavesPanel from './RoiSavesPanel';
import styles from './StatisticsTab.module.css';

type SubTab = 'overview' | 'quotes' | 'response' | 'salespersons' | 'routing' | 'inbox-response' | 'costs' | 'orders' | 'email-pipeline' | 'system-health' | 'data-freshness' | 'doc-pipeline' | 'roi';
type DateRange = '1y' | '2y' | 'all';

function InfoTooltip({ text }: { text: string }): React.ReactElement {
  const [show, setShow] = useState(false);
  return (
    <span
      className={styles.infoTooltipWrapper}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <span className={styles.infoIcon}>i</span>
      {show && <span className={styles.infoTooltip}>{text}</span>}
    </span>
  );
}

function formatChange(value: number | null | undefined, inverted = false): React.ReactElement | null {
  if (value == null) return null;
  const direction = getChangeDirection(value, inverted);
  const className = direction === 'positive'
    ? styles.kpiChangePositive
    : direction === 'negative'
      ? styles.kpiChangeNegative
      : styles.kpiChangeNeutral;
  const arrow = getChangeArrow(value);
  return (
    <span className={`${styles.kpiChange} ${className}`}>
      {arrow} {Math.abs(value).toFixed(1)}% vs prior period
    </span>
  );
}

function getDateRange(range: DateRange): { from: Date; to: Date } {
  const to = new Date();
  const from = new Date();
  if (range === '1y') from.setFullYear(from.getFullYear() - 1);
  else if (range === '2y') from.setFullYear(from.getFullYear() - 2);
  else from.setFullYear(2020); // "all time"
  return { from, to };
}

export default function StatisticsTab() {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('overview');
  const [dateRange, setDateRange] = useState<DateRange>('2y');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [selectedSalesperson, setSelectedSalesperson] = useState<string>('');
  const [selectedSegment, setSelectedSegment] = useState<string>('');

  // Data state
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [winRateTrend, setWinRateTrend] = useState<TrendDataPoint[]>([]);
  const [responseTrend, setResponseTrend] = useState<TrendDataPoint[]>([]);
  const [salespersons, setSalespersons] = useState<SalespersonStats[]>([]);
  const [pipeline, setPipeline] = useState<QuotePipelineStats | null>(null);
  const [routing, setRouting] = useState<RoutingSummary | null>(null);

  // Load filter options once on mount
  useEffect(() => {
    statisticsService.getFilterOptions().then(setFilterOptions).catch(() => {});
  }, []);

  const hasFilters = !!selectedSalesperson || !!selectedSegment;

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { from, to } = getDateRange(dateRange);

    const currentFilters: StatsFilters = {
      salesperson_no: selectedSalesperson || undefined,
      market_segment: selectedSegment || undefined,
    };

    try {
      const results = await Promise.allSettled([
        statisticsService.getOverview(from, to, currentFilters),
        statisticsService.getQuoteWinRate(from, to, 'month', currentFilters),
        statisticsService.getResponseTimes(from, to),
        statisticsService.getSalespersonPerformance(from, to, 10, currentFilters),
        statisticsService.getQuotePipeline(from, to, currentFilters),
        statisticsService.getRoutingSummary(from, to),
      ]);

      const [ov, wr, rt, sp, pl, rs] = results;
      if (ov.status === 'fulfilled') setOverview(ov.value);
      if (wr.status === 'fulfilled') setWinRateTrend(wr.value);
      if (rt.status === 'fulfilled') setResponseTrend(rt.value);
      if (sp.status === 'fulfilled') setSalespersons(sp.value);
      if (pl.status === 'fulfilled') setPipeline(pl.value);
      if (rs.status === 'fulfilled') setRouting(rs.value);

      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length === results.length) {
        setError('Failed to load statistics');
      } else if (failures.length > 0) {
        setError(`Some data could not be loaded (${failures.length} of ${results.length} requests failed)`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedSalesperson, selectedSegment]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const subTabs: { key: SubTab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'roi', label: 'ROI — Saves' },
    { key: 'quotes', label: 'Quotes' },
    { key: 'response', label: 'Response Times' },
    { key: 'salespersons', label: 'Sales Reps' },
    { key: 'routing', label: 'Email Routing' },
    { key: 'inbox-response', label: 'Inbox Response' },
    { key: 'orders', label: 'Order Detection' },
    { key: 'email-pipeline', label: 'Email Pipeline' },
    { key: 'costs', label: 'AI Costs' },
    { key: 'system-health', label: 'System Health' },
    { key: 'data-freshness', label: 'Data Freshness' },
    { key: 'doc-pipeline', label: 'Doc Pipeline' },
  ];

  return (
    <div className={styles.container}>
      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <label>Date Range:</label>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value as DateRange)}
        >
          <option value="1y">Last 12 Months</option>
          <option value="2y">Last 2 Years</option>
          <option value="all">All Time</option>
        </select>

        {filterOptions && (
          <>
            <span className={styles.filterDivider} />
            <label>Sales Rep:</label>
            <select
              value={selectedSalesperson}
              onChange={(e) => setSelectedSalesperson(e.target.value)}
            >
              <option value="">All Reps</option>
              {filterOptions.salespersons.map((sp) => (
                <option key={sp.salesperson_no} value={sp.salesperson_no}>
                  {sp.name}
                </option>
              ))}
            </select>

            <label>Segment:</label>
            <select
              value={selectedSegment}
              onChange={(e) => setSelectedSegment(e.target.value)}
            >
              <option value="">All Segments</option>
              {filterOptions.market_segments.map((seg) => (
                <option key={seg.value} value={seg.value}>
                  {seg.value} ({seg.count})
                </option>
              ))}
            </select>

            {hasFilters && (
              <button
                className={styles.clearFilters}
                onClick={() => {
                  setSelectedSalesperson('');
                  setSelectedSegment('');
                }}
              >
                Clear Filters
              </button>
            )}
          </>
        )}
      </div>

      {hasFilters && (
        <div className={styles.filterNotice}>
          Showing filtered results
          {selectedSalesperson && filterOptions && (
            <span>
              {' '}for <strong>{filterOptions.salespersons.find(s => s.salesperson_no === selectedSalesperson)?.name || selectedSalesperson}</strong>
            </span>
          )}
          {selectedSegment && (
            <span>
              {' '}in <strong>{selectedSegment}</strong> segment
            </span>
          )}
          . Response Times and Email Routing tabs show company-wide data.
        </div>
      )}

      {/* Sub-tabs */}
      <div className={styles.subTabs}>
        {subTabs.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.subTab} ${activeSubTab === tab.key ? styles.subTabActive : ''}`}
            onClick={() => setActiveSubTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <div className={styles.loading}>Loading statistics...</div>}
      {error && <div className={styles.error}>{error}</div>}

      {!loading && (
        <>
          {activeSubTab === 'overview' && overview && (
            <OverviewPanel
              overview={overview}
              pipeline={pipeline}
            />
          )}
          {activeSubTab === 'quotes' && (
            <QuotesPanel trend={winRateTrend} pipeline={pipeline} />
          )}
          {activeSubTab === 'response' && (
            <ResponsePanel trend={responseTrend} overview={overview} />
          )}
          {activeSubTab === 'salespersons' && (
            <SalespersonsPanel salespersons={salespersons} />
          )}
          {activeSubTab === 'routing' && routing && (
            <RoutingPanel routing={routing} />
          )}
          {activeSubTab === 'inbox-response' && (
            <InboxResponseTimePanel dateFrom={getDateRange(dateRange).from} dateTo={getDateRange(dateRange).to} />
          )}
          {activeSubTab === 'orders' && (
            <OrdersPanel dateFrom={getDateRange(dateRange).from} dateTo={getDateRange(dateRange).to} />
          )}
          {activeSubTab === 'email-pipeline' && (
            <EmailPipelinePanel />
          )}
          {activeSubTab === 'costs' && (
            <CostsPanel dateFrom={getDateRange(dateRange).from} dateTo={getDateRange(dateRange).to} />
          )}
          {activeSubTab === 'system-health' && <SystemHealthPanel />}
          {activeSubTab === 'data-freshness' && <DataFreshnessPanel />}
          {activeSubTab === 'doc-pipeline' && <DocumentPipelinePanel />}
          {activeSubTab === 'roi' && <RoiSavesPanel />}
        </>
      )}
    </div>
  );
}

// --- Sub-panels ---

function OverviewPanel({ overview, pipeline }: { overview: OverviewStats; pipeline: QuotePipelineStats | null }) {
  return (
    <>
      <div className={styles.kpiGrid}>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>
            Quote Win Rate
            <InfoTooltip text="Percentage of quotes where a matching order was found in Sage (won_from_order = true) out of all quotes created in the selected period." />
          </span>
          <span className={styles.kpiValue}>{overview.win_rate}%</span>
          {formatChange(overview.win_rate_change)}
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>
            Avg Response Time
            <InfoTooltip text="Average time between email receipt and routing result creation for matched emails. Measures routing processing delay, not human reply time." />
          </span>
          <span className={styles.kpiValue}>
            {overview.response_time_avg != null ? `${overview.response_time_avg}h` : 'N/A'}
          </span>
          {formatChange(overview.response_time_change, true)}
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>
            Routing Accuracy
            <InfoTooltip text="Percentage of non-skipped emails that were successfully matched to a customer/salesperson. Calculated as MATCHED / (total - SKIPPED)." />
          </span>
          <span className={styles.kpiValue}>
            {overview.routing_accuracy != null ? `${overview.routing_accuracy}%` : 'N/A'}
          </span>
          {formatChange(overview.routing_accuracy_change)}
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>
            Revenue
            <InfoTooltip text="Total invoiced revenue from Sage sales history (extension_amt) within the selected date range." />
          </span>
          <span className={styles.kpiValue}>{formatCurrency(overview.revenue_ytd)}</span>
          {formatChange(overview.revenue_ytd_change)}
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>
            Active Quotes
            <InfoTooltip text="Number of quotes with status 'Open' — currently active and not yet expired or won." />
          </span>
          <span className={styles.kpiValue}>{overview.active_quotes}</span>
        </div>
      </div>

      {pipeline && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Quote Pipeline</div>
          <PipelineBar pipeline={pipeline} />
        </div>
      )}
    </>
  );
}

function QuotesPanel({ trend, pipeline }: { trend: TrendDataPoint[]; pipeline: QuotePipelineStats | null }) {
  const maxVal = Math.max(...trend.map((t) => t.value), 1);

  return (
    <>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Win Rate Trend</div>
        <div className={styles.trendList}>
          {trend.map((point) => (
            <div key={point.period} className={styles.trendRow}>
              <span className={styles.trendPeriod}>{point.period}</span>
              <div className={styles.trendBar}>
                <div
                  className={styles.trendBarFill}
                  style={{ width: `${(point.value / maxVal) * 100}%` }}
                />
              </div>
              <span className={styles.trendValue}>{point.value}%</span>
            </div>
          ))}
        </div>
      </div>

      {pipeline && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Pipeline Breakdown</div>
          <PipelineBar pipeline={pipeline} />
        </div>
      )}
    </>
  );
}

function ResponsePanel({ trend, overview }: { trend: TrendDataPoint[]; overview: OverviewStats | null }) {
  const maxVal = Math.max(...trend.map((t) => t.value), 1);

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        Response Time Trend
        {overview?.response_time_avg != null && (
          <span style={{ fontWeight: 400, fontSize: '0.875rem', marginLeft: '0.5rem', color: '#64748b' }}>
            (Avg: {overview.response_time_avg}h)
          </span>
        )}
      </div>
      <div className={styles.trendList}>
        {trend.map((point) => (
          <div key={point.period} className={styles.trendRow}>
            <span className={styles.trendPeriod}>{point.period}</span>
            <div className={styles.trendBar}>
              <div
                className={styles.trendBarFill}
                style={{ width: `${(point.value / maxVal) * 100}%` }}
              />
            </div>
            <span className={styles.trendValue}>{point.value}h</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SalespersonsPanel({ salespersons }: { salespersons: SalespersonStats[] }) {
  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>Salesperson Performance</div>
      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Name</th>
            <th className={styles.numericCell}>Quotes</th>
            <th className={styles.numericCell}>Win Rate</th>
            <th className={styles.numericCell}>Revenue</th>
          </tr>
        </thead>
        <tbody>
          {salespersons.map((sp) => (
            <tr key={sp.salesperson_no}>
              <td>{sp.name}</td>
              <td className={styles.numericCell}>{sp.quotes_count}</td>
              <td className={styles.numericCell}>{sp.win_rate}%</td>
              <td className={styles.numericCell}>{formatCurrency(sp.revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoutingPanel({ routing }: { routing: RoutingSummary }) {
  return (
    <>
      <div className={styles.kpiGrid}>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Total Processed</span>
          <span className={styles.kpiValue}>{routing.total_processed.toLocaleString()}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Match Rate</span>
          <span className={styles.kpiValue}>{routing.match_rate}%</span>
          {formatChange(routing.match_rate_change)}
        </div>
        {routing.accuracy != null && (
          <div className={styles.kpiCard}>
            <span className={styles.kpiLabel}>Accuracy</span>
            <span className={styles.kpiValue}>{routing.accuracy}%</span>
            {formatChange(routing.accuracy_change)}
          </div>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Routing Status Breakdown</div>
        <div className={styles.statusGrid}>
          {Object.entries(routing.by_status).map(([status, count]) => (
            <div key={status} className={styles.statusItem}>
              <div className={styles.statusItemValue}>{count.toLocaleString()}</div>
              <div className={styles.statusItemLabel}>{status}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function PipelineBar({ pipeline }: { pipeline: QuotePipelineStats }) {
  const maxVal = Math.max(pipeline.won_value, pipeline.pending_value, pipeline.expired_value, pipeline.lost_value, 1);

  const bars = [
    { label: 'Won', value: pipeline.won_value, count: pipeline.won_count, className: styles.barFillWon },
    { label: 'Pending', value: pipeline.pending_value, count: pipeline.pending_count, className: styles.barFillPending },
    { label: 'Expired', value: pipeline.expired_value, count: pipeline.expired_count, className: styles.barFillExpired },
    { label: 'Lost', value: pipeline.lost_value, count: pipeline.lost_count, className: styles.barFillLost },
  ];

  return (
    <div className={styles.barChart}>
      {bars.map((bar) => (
        <div key={bar.label} className={styles.barRow}>
          <span className={styles.barLabel}>{bar.label}</span>
          <div className={styles.barTrack}>
            <div
              className={`${styles.barFill} ${bar.className}`}
              style={{ width: `${(bar.value / maxVal) * 100}%` }}
            />
          </div>
          <span className={styles.barValue}>
            {formatCurrency(bar.value)} ({bar.count})
          </span>
        </div>
      ))}
    </div>
  );
}
